import React, { useState, useEffect, useMemo } from 'react';
import apiClient from '../utils/api';

interface PredictionStats {
  totalPredictions: number;
  totalPoints: number;
  scoredPredictions: number;
  bankerPredictions: number;
  exactScores?: number;
  correctResults?: number;
  correctBanks?: number;
  predictionAccuracy?: number;
}

interface Season {
  id: string;
  name: string;
  startDate?: string;
  competitionId?: string | null;
  order?: number;
}

interface PredictionLike {
  points?: number;
  isScored: boolean;
  isBanker: boolean;
  exactScorePoints?: number;
  correctResultPoints?: number;
  season: {
    id?: string;
    name: string;
    order?: number;
    competitionId?: string | null;
    startDate?: string;
  };
}

interface MyPredictionsSummaryProps {
  /** Whether to show the header with title and "View All" link */
  showHeader?: boolean;
  /** Pre-calculated stats (legacy — shows aggregate without tabs) */
  stats?: PredictionStats;
  /** Pre-fetched predictions to avoid refetching */
  predictions?: PredictionLike[];
  /** Pre-fetched seasons (used with predictions to build competition tabs) */
  seasons?: Season[];
  /** Custom className for the container */
  className?: string;
  /** Whether to show the component in compact mode */
  compact?: boolean;
  /** User ID to fetch stats for (if not provided, uses current user) */
  userId?: string;
}

const CACHE_TTL_MS = 15 * 60 * 1000;

const isExcludedFromPerformanceTabs = (season: Pick<Season, 'name'>): boolean =>
  /world cup/i.test(season.name);

const calculateStats = (predictions: PredictionLike[]): PredictionStats => {
  const totalPredictions = predictions.length;
  const totalPoints = predictions.reduce((sum, p) => sum + (p.points || 0), 0);
  const scoredPredictions = predictions.filter(p => p.isScored).length;
  const bankerPredictions = predictions.filter(p => p.isBanker).length;
  const exactScores = predictions.filter(p => (p.exactScorePoints || 0) > 0).length;
  const correctResults = predictions.filter(
    p => (p.exactScorePoints || 0) > 0 || (p.correctResultPoints || 0) > 0
  ).length;
  const correctBanks = predictions.filter(
    p => p.isBanker && ((p.exactScorePoints || 0) > 0 || (p.correctResultPoints || 0) > 0)
  ).length;
  const predictionAccuracy = scoredPredictions > 0
    ? Math.round((correctResults / scoredPredictions) * 100)
    : 0;

  return {
    totalPredictions,
    totalPoints,
    scoredPredictions,
    bankerPredictions,
    exactScores,
    correctResults,
    correctBanks,
    predictionAccuracy,
  };
};

const getVisibleSeasons = (seasons: Season[], participatedSeasonIds: string[]): Season[] => {
  const participated = new Set(participatedSeasonIds);
  const latestByCompetition = new Map<string, Season>();

  seasons
    .filter(season => participated.has(season.id))
    .filter(season => !isExcludedFromPerformanceTabs(season))
    .forEach(season => {
      const key = season.competitionId || season.id;
      const existing = latestByCompetition.get(key);
      if (!existing || new Date(season.startDate || 0) > new Date(existing.startDate || 0)) {
        latestByCompetition.set(key, season);
      }
    });

  return Array.from(latestByCompetition.values()).sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );
};

const buildSeasonCatalog = (seasons: Season[], predictions: PredictionLike[]): Season[] => {
  const catalog = [...seasons];
  const knownIds = new Set(catalog.map(season => season.id));

  predictions.forEach(prediction => {
    const { season } = prediction;
    if (!season.id || knownIds.has(season.id) || isExcludedFromPerformanceTabs(season)) return;

    catalog.push({
      id: season.id,
      name: season.name,
      order: season.order,
      competitionId: season.competitionId,
      startDate: season.startDate,
    });
    knownIds.add(season.id);
  });

  return catalog;
};

const getParticipatedSeasonIds = (
  predictions: PredictionLike[],
  participatedSeasonIds: string[]
): string[] => {
  const fromPredictions = predictions
    .filter(prediction => !isExcludedFromPerformanceTabs(prediction.season))
    .map(prediction => prediction.season.id)
    .filter((id): id is string => Boolean(id));

  if (fromPredictions.length > 0) {
    return Array.from(new Set(fromPredictions));
  }

  return participatedSeasonIds;
};

const statCellClass = 'flex flex-col items-center justify-center text-center';

const StatsGrid: React.FC<{ stats: PredictionStats; compact: boolean }> = ({ stats, compact }) => (
  <div className={`grid items-stretch gap-2 md:gap-4 ${compact ? 'grid-cols-2 md:grid-cols-6' : 'grid-cols-2 md:grid-cols-6'}`}>
    <div className={`${statCellClass} rounded-lg bg-black/12`}>
      <div className="text-2xl font-extrabold dark-text">{stats.totalPoints}</div>
      <div className="text-sm font-semibold dark-text">Total Points</div>
    </div>
    <div className={statCellClass}>
      <div className="text-2xl font-bold dark-text">{stats.correctResults}</div>
      <div className="text-sm dark-text">Correct Results</div>
    </div>
    <div className={statCellClass}>
      <div className="text-2xl font-bold dark-text">{stats.exactScores}</div>
      <div className="text-sm dark-text">Exact Scores</div>
    </div>
    <div className={statCellClass}>
      <div className="text-2xl font-bold dark-text">{stats.scoredPredictions}</div>
      <div className="text-sm dark-text">Predictions Made</div>
    </div>
    <div className={statCellClass}>
      <div className="text-2xl font-bold dark-text">{stats.correctBanks}</div>
      <div className="text-sm dark-text">Correct Bankers</div>
    </div>
    <div className={statCellClass}>
      <div className="text-2xl font-bold dark-text">{stats.predictionAccuracy}%</div>
      <div className="text-sm dark-text">Prediction Accuracy</div>
    </div>
  </div>
);

const MyPredictionsSummary: React.FC<MyPredictionsSummaryProps> = ({
  showHeader = true,
  stats: providedStats,
  predictions: providedPredictions,
  seasons: providedSeasons,
  className = '',
  compact = false,
  userId,
}) => {
  const [predictions, setPredictions] = useState<PredictionLike[]>(providedPredictions || []);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [participatedSeasonIds, setParticipatedSeasonIds] = useState<string[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<string>('');
  const [seasonTabsScrollLeft, setSeasonTabsScrollLeft] = useState(0);
  const [loading, setLoading] = useState(!providedStats && !providedPredictions);
  const [error, setError] = useState<string | null>(null);

  const useLegacyStats = Boolean(providedStats && !providedPredictions);

  useEffect(() => {
    if (providedPredictions) {
      setPredictions(providedPredictions);
    }

    if (providedStats && !providedPredictions) {
      setLoading(false);
      return;
    }

    if (providedPredictions && providedSeasons) {
      setSeasons(providedSeasons);
      setParticipatedSeasonIds(
        Array.from(new Set(providedPredictions.map(p => p.season.id).filter((id): id is string => Boolean(id))))
      );
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      const cacheKey = userId ? `user_predictions_data_v2_${userId}` : 'my_predictions_summary_data';
      const cacheTimestampKey = userId
        ? `user_predictions_data_ts_v2_${userId}`
        : 'my_predictions_summary_data_ts';

      const cachedData = sessionStorage.getItem(cacheKey);
      const cacheTimestamp = sessionStorage.getItem(cacheTimestampKey);

      if (cachedData && cacheTimestamp) {
        try {
          const timestamp = parseInt(cacheTimestamp, 10);
          if (Date.now() - timestamp < CACHE_TTL_MS) {
            const parsed = JSON.parse(cachedData);
            setPredictions(parsed.predictions);
            setSeasons(parsed.seasons);
            setParticipatedSeasonIds(parsed.participatedSeasonIds);
            setLoading(false);
            return;
          }
          sessionStorage.removeItem(cacheKey);
          sessionStorage.removeItem(cacheTimestampKey);
        } catch {
          sessionStorage.removeItem(cacheKey);
          sessionStorage.removeItem(cacheTimestampKey);
        }
      }

      try {
        const predictionsEndpoint = userId ? `/users/${userId}/predictions` : '/predictions';
        const [predictionsRes, seasonsRes, participatedRes] = await Promise.all([
          providedPredictions
            ? Promise.resolve({ data: providedPredictions })
            : apiClient.get(predictionsEndpoint),
          apiClient.get('/seasons'),
          userId || providedPredictions
            ? Promise.resolve({ data: [] as string[] })
            : apiClient.get('/predictions/participated-seasons'),
        ]);

        const fetchedPredictions = predictionsRes.data;
        const fetchedSeasons = seasonsRes.data;
        const fetchedParticipated = userId || providedPredictions
          ? getParticipatedSeasonIds(fetchedPredictions, [])
          : participatedRes.data;

        if (!providedPredictions) {
          setPredictions(fetchedPredictions);
        }
        setSeasons(fetchedSeasons);
        setParticipatedSeasonIds(fetchedParticipated);

        if (!userId && !providedPredictions) {
          sessionStorage.setItem(
            cacheKey,
            JSON.stringify({
              predictions: fetchedPredictions,
              seasons: fetchedSeasons,
              participatedSeasonIds: fetchedParticipated,
            })
          );
          sessionStorage.setItem(cacheTimestampKey, Date.now().toString());
        }
      } catch (err) {
        console.error('Error fetching prediction stats:', err);
        setError('Failed to load prediction stats');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [providedStats, providedPredictions, providedSeasons, userId]);

  useEffect(() => {
    if (providedPredictions) {
      setPredictions(providedPredictions);
      setParticipatedSeasonIds(
        Array.from(new Set(providedPredictions.map(p => p.season.id).filter((id): id is string => Boolean(id))))
      );
    }
    if (providedSeasons) {
      setSeasons(providedSeasons);
    }
  }, [providedPredictions, providedSeasons]);

  const visibleSeasons = useMemo((): Season[] => {
    if (useLegacyStats) return [];

    const participatedIds = getParticipatedSeasonIds(predictions, participatedSeasonIds);
    const seasonCatalog = buildSeasonCatalog(seasons, predictions);
    return getVisibleSeasons(seasonCatalog, participatedIds);
  }, [useLegacyStats, predictions, seasons, participatedSeasonIds]);

  const showAllCompetitionsTab = visibleSeasons.length >= 2;

  useEffect(() => {
    if (useLegacyStats || visibleSeasons.length === 0) return;

    const isValidSelection =
      selectedSeasonId === 'all'
        ? showAllCompetitionsTab
        : visibleSeasons.some(season => season.id === selectedSeasonId);

    if (!isValidSelection) {
      setSelectedSeasonId(visibleSeasons[0].id);
    }
  }, [useLegacyStats, visibleSeasons, selectedSeasonId, showAllCompetitionsTab]);

  const eligiblePredictions = useMemo(
    () => predictions.filter(prediction => !isExcludedFromPerformanceTabs(prediction.season)),
    [predictions]
  );

  const filteredPredictions = useMemo(() => {
    if (useLegacyStats) return [];

    const seasonId = selectedSeasonId || visibleSeasons[0]?.id || 'all';
    if (seasonId === 'all') return eligiblePredictions;

    return eligiblePredictions.filter(p => p.season.id === seasonId);
  }, [useLegacyStats, eligiblePredictions, selectedSeasonId, visibleSeasons]);

  const stats = useMemo(() => {
    if (useLegacyStats && providedStats) return providedStats;
    return calculateStats(filteredPredictions);
  }, [useLegacyStats, providedStats, filteredPredictions]);

  const handleSeasonTabsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setSeasonTabsScrollLeft(e.currentTarget.scrollLeft);
  };

  const getTabClass = (isActive: boolean) =>
    `h-10 px-4 flex items-center text-sm whitespace-nowrap flex-shrink-0 transition-colors rounded-t-lg ${
      isActive
        ? 'font-bold dark-text bg-highlight relative z-10'
        : 'font-medium opacity-70 hover:opacity-100'
    }`;

  const panelClass = (withTabs: boolean, roundTopLeft = false) =>
    `shadow overflow-hidden bg-highlight ${className} ${
      withTabs
        ? `rounded-b-lg rounded-tr-lg ${roundTopLeft ? 'rounded-tl-lg' : '!rounded-tl-none'}`
        : 'card rounded-lg'
    }`;

  const renderTabs = () => (
    <div className="relative min-w-0 overflow-hidden">
      {visibleSeasons.length > 3 && seasonTabsScrollLeft > 0 && (
        <button
          onClick={() => {
            const container = document.getElementById('summary-season-tabs');
            if (container) {
              container.scrollBy({ left: -200, behavior: 'smooth' });
            }
          }}
          className="absolute left-0 top-0 bottom-0 z-20 w-8 flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity bg-gradient-to-r from-[var(--bg-primary)] to-transparent"
          aria-label="Scroll tabs left"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--text-primary)' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      <div
        id="summary-season-tabs"
        className="flex h-10 items-end gap-1 overflow-x-auto scrollbar-hide min-w-0 pr-6"
        onScroll={handleSeasonTabsScroll}
      >
        {visibleSeasons.map(season => {
          const isActive =
            selectedSeasonId === season.id ||
            (!selectedSeasonId && season.id === visibleSeasons[0]?.id);
          return (
            <button
              key={season.id}
              onClick={() => setSelectedSeasonId(season.id)}
              className={getTabClass(isActive)}
              style={isActive ? undefined : { color: 'var(--text-secondary)' }}
            >
              {season.name}
            </button>
          );
        })}
        {showAllCompetitionsTab && (
          <button
            onClick={() => setSelectedSeasonId('all')}
            className={getTabClass(selectedSeasonId === 'all')}
            style={selectedSeasonId === 'all' ? undefined : { color: 'var(--text-secondary)' }}
          >
            All Competitions
          </button>
        )}
      </div>

      {visibleSeasons.length > 3 && (
        <button
          onClick={() => {
            const container = document.getElementById('summary-season-tabs');
            if (container) {
              container.scrollBy({ left: 200, behavior: 'smooth' });
            }
          }}
          className="absolute right-0 top-0 bottom-0 z-20 w-8 flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity bg-gradient-to-l from-[var(--bg-primary)] to-transparent"
          aria-label="Scroll tabs right"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--text-primary)' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}
    </div>
  );

  if (loading) {
    return (
      <>
        {showHeader && (
          <div className="flex justify-between items-center mb-4">
            <div className="h-6 w-40 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
          </div>
        )}
        <div className="animate-pulse">
          <div className="flex h-10 items-end gap-2 mb-0">
            <div className="h-10 w-36 bg-black/10 rounded-t-lg" />
            <div className="h-10 w-40 rounded-t-lg opacity-40" style={{ backgroundColor: 'var(--text-secondary)' }} />
          </div>
          <div className={`${panelClass(true)} p-6`}>
            <div className={`grid gap-2 md:gap-4 ${compact ? 'grid-cols-2 md:grid-cols-6' : 'grid-cols-1 md:grid-cols-6'}`}>
              {[...Array(6)].map((_, i) => (
                <div key={i} className="text-center">
                  <div className="h-8 bg-black/10 rounded mb-2" />
                  <div className="h-4 bg-black/10 rounded" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error || stats.totalPredictions === 0) {
    return null;
  }

  const showTabs = !useLegacyStats && visibleSeasons.length > 1;
  const activeSeasonId = selectedSeasonId || visibleSeasons[0]?.id || 'all';
  const roundPanelTopLeft =
    showTabs && visibleSeasons.length > 0 && activeSeasonId !== visibleSeasons[0].id;

  return (
    <>
      {showHeader && !userId && (
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
            My Performance
          </h2>
        </div>
      )}

      <div className="min-w-0 max-w-full">
        {showTabs && renderTabs()}

        <div className={`${panelClass(showTabs, roundPanelTopLeft)} p-6 ${showTabs ? '-mt-px' : ''}`}>
          <StatsGrid stats={stats} compact={compact} />
        </div>
      </div>
    </>
  );
};

export default MyPredictionsSummary;
